/**
 * Items Store
 * Manages collection items state and UI state for content views
 */

import { create } from 'zustand';
import type { Item, ItemsQuery, ItemsMeta, PrimaryKey } from '@/hooks/useItems';

// Re-export types for convenience
export type { Item, ItemsQuery, ItemsMeta, PrimaryKey };

interface ItemsState {
    // Current view state
    collection: string | null;
    items: Item[];
    meta: ItemsMeta | null;
    loading: boolean;
    error: string | null;

    // Selection
    selectedItems: PrimaryKey[];

    // Query state
    query: ItemsQuery;

    // Actions
    setCollection: (collection: string) => void;
    setItems: (items: Item[], meta?: ItemsMeta) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;

    // Selection actions
    selectItem: (id: PrimaryKey) => void;
    deselectItem: (id: PrimaryKey) => void;
    selectAll: () => void;
    deselectAll: () => void;
    toggleSelection: (id: PrimaryKey) => void;

    // Query actions
    setQuery: (query: Partial<ItemsQuery>) => void;
    setSearch: (search: string) => void;
    setSort: (sort: string[]) => void;
    setFilter: (filter: Record<string, unknown>) => void;
    setPage: (page: number) => void;
    setLimit: (limit: number) => void;
    resetQuery: () => void;
}

const defaultQuery: ItemsQuery = {
    fields: ['*'],
    sort: [],
    limit: 25,
    page: 1,
    meta: ['total_count', 'filter_count'],
};

export const useItemsStore = create<ItemsState>((set, get) => ({
    // Initial state
    collection: null,
    items: [],
    meta: null,
    loading: false,
    error: null,
    selectedItems: [],
    query: { ...defaultQuery },

    // Actions
    setCollection: (collection) => {
        const { collection: currentCollection } = get();
        if (currentCollection === collection) {
            // Same collection (e.g. returning from a detail page) — preserve the
            // search / filter / sort state so the user lands back where they were.
            // Only reset the items cache and selection.
            set({ selectedItems: [], items: [], meta: null, error: null });
            return;
        }
        set({
            collection,
            items: [],
            meta: null,
            selectedItems: [],
            query: { ...defaultQuery },
            error: null,
        });
    },

    setItems: (items, meta) => {
        set({ items, meta: meta ?? null, loading: false, error: null });
    },

    setLoading: (loading) => {
        set({ loading });
    },

    setError: (error) => {
        set({ error, loading: false });
    },

    // Selection actions
    selectItem: (id) => {
        const { selectedItems } = get();
        if (!selectedItems.includes(id)) {
            set({ selectedItems: [...selectedItems, id] });
        }
    },

    deselectItem: (id) => {
        const { selectedItems } = get();
        set({ selectedItems: selectedItems.filter((i) => i !== id) });
    },

    selectAll: () => {
        const { items } = get();
        const primaryKey = 'id'; // Default primary key
        set({ selectedItems: items.map((item) => item[primaryKey] as PrimaryKey).filter(Boolean) });
    },

    deselectAll: () => {
        set({ selectedItems: [] });
    },

    toggleSelection: (id) => {
        const { selectedItems } = get();
        if (selectedItems.includes(id)) {
            set({ selectedItems: selectedItems.filter((i) => i !== id) });
        } else {
            set({ selectedItems: [...selectedItems, id] });
        }
    },

    // Query actions
    setQuery: (query) => {
        set((state) => ({
            query: { ...state.query, ...query },
        }));
    },

    setSearch: (search) => {
        set((state) => ({
            query: { ...state.query, search, page: 1 },
        }));
    },

    setSort: (sort) => {
        set((state) => ({
            query: { ...state.query, sort },
        }));
    },

    setFilter: (filter) => {
        set((state) => ({
            query: { ...state.query, filter, page: 1 },
        }));
    },

    setPage: (page) => {
        set((state) => ({
            query: { ...state.query, page },
        }));
    },

    setLimit: (limit) => {
        set((state) => ({
            query: { ...state.query, limit, page: 1 },
        }));
    },

    resetQuery: () => {
        set({ query: { ...defaultQuery } });
    },
}));
